import {useLocation} from "react-router-dom";
import React, {useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import {
    Checkbox,
    Tooltip,
    Typography
} from "@mui/material";
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner
} from "material-react-table";
import MenuItem from "@mui/material/MenuItem";
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import AllOrganizationService from "../../../API/admin/AllOrganizationService";
import AllCustomerService from "../../../API/admin/AllCustomerService";
import AllUserService from "../../../API/admin/AllUserService";
import {EditLocalStoragePagesData} from "../../../utils/EditLocalStoragePagesData";
import {country} from "../../../data/Country";
import DateTimeUtil from "../../../utils/common/DateTimeUtil";
import ModalConfirm from "../../../components/UI/modal/ModalConfirm";
import NameModal from "../../../components/UI/modal/NameModal";
import RatingService from "../../../API/admin/RatingService";

const AllUsers = ({itemId, handleModalAlert}) => {
    const pathname = useLocation().pathname
    const [data, setData] = useState([]);
    const [modalConfirm, setModalConfirm] = useState(false);
    const [currentEditRowData, setCurrentEditRowData] = useState(null);
    const api = itemId === "allOrganizations" ? AllOrganizationService : itemId === "allCustomers" ? AllCustomerService : AllUserService
    const pageName = itemId === "allOrganizations" ? "Организации" : itemId === "allCustomers" ? "Заказчики" : "Пользователи"
    const [modal, setModal] = useState(false);
    const [loading, setLoading] = useState(false)

    const [tableOptions, setTableOptions] = useState({
        pageIndex: 0,
        pageSize: 100,
        totalElements: 0,
        searchData: ""
    })

    useEffect(() => {
        getStartPageSize()
        api.getAll().then(function (response) {
            setData(response.data);
            setTableOptions((prev) => ({...prev, totalElements: response?.data.length}))
        })
            .catch(function (error) {
                handleModalAlert(error);
            });
    }, [itemId]);


    const tableOptionsHandle = (nextState) => {
        setTableOptions(nextState)
        if (nextState.pageSize !== tableOptions.pageSize) {
            EditLocalStoragePagesData(`${pathname}+${itemId}`, "pageSize", nextState.pageSize)
        }
    }

    const getStartPageSize = () => {
        const pagesData = JSON.parse(localStorage.getItem('pagesData')) || {}
        const pageSize = pagesData[`${pathname}+${itemId}`]?.pageSize
        pageSize && setTableOptions((prev) => ({...prev, pageSize: pageSize}))
    }
    const handleEdit = (row) => {
        setCurrentEditRowData(row);
        setModal(true)
    }
    const handleUpdate = (value) => {
        setLoading(true)
        AllOrganizationService.updateMaxUserAmount(currentEditRowData.id, value.name)
            .then(function (response) {
                setData(prev => prev.map(el => el.id === response.data.id ? response.data : el))
                handleClose()
                handleModalAlert("Данные изменены", 'success')
            })
            .catch(function (error) {
                handleModalAlert(error)
            })
            .finally(() => setLoading(false))
    }
    const handleConfirm = (row) => {
        setModalConfirm(true)
        setCurrentEditRowData(row)
    }
    const handleClose = () => {
        setModalConfirm(false)
        setModal(false)
        setCurrentEditRowData(null)
    }

    const handleDelete = () => {
        api.delete(currentEditRowData.id)
            .then(function () {
                setData(prev => prev.filter(el => el.id !== currentEditRowData.id))
                handleClose()
                tableOptionsHandle((prev) => ({...prev, totalElements: data.length - 1}))
                handleModalAlert("Данные удалены", 'success')
            })
            .catch(function (error) {
                handleModalAlert(error)
            })
    };

    const handleUnable = (row) => {
        api.update(row.id, !row.enabled).then(function (response) {
            setData(prev => prev.map(el => el.id === row.id ? {...el, enabled: !el.enabled} : el));
            handleClose()
            handleModalAlert("Данные изменены", 'success')
        })
            .catch(function (error) {
                handleModalAlert(error);
            });
    }

    const columns = useMemo(() => {
        const baseColumns = [
            {
                id: "name",
                header: 'Наименование',
                accessorKey: itemId === "allUsers" ? "userName" : "name",
                size: 8,
                maxSize: 8,
            },
            {
                id: 'email',
                header: 'Email',
                accessorKey: 'email',
                minSize: 250
            },
            {
                header: 'Доступно',
                accessorKey: 'enabled',
                enableColumnActions: false,
                enableEditing: false,
                size: 8,
                Cell: ({cell, row}) => <Checkbox
                    cell={cell}
                    checked={row.original.enabled}
                    onChange={() => handleUnable(row.original)}
                    sx={{padding: "7px", '& .MuiSvgIcon-root': {fontSize: 22}}}
                >
                </Checkbox>,
            },
        ]
        if (itemId !== "allUsers") {
            baseColumns.splice(2, 0, {
                id: 'registrationNumber',
                header: 'Регистрационный номер',
                accessorKey: 'registrationNumber',
                size: 8,
                maxSize: 8,
            },)
            baseColumns.splice(2, 0, {
                id: 'organizationalAndLegalForm',
                header: 'ОПФ',
                accessorKey: 'organizationalAndLegalForm',
                minSize: 6,
                maxSize: 6,
            },)
            baseColumns.splice(2, 0, {
                header: 'Адрес',
                id: 'address',
                accessorKey: 'address',
                Cell: ({cell, row}) => {
                    const address = cell.getValue()
                    const addressString = address && [country[address?.country], address?.locality, address?.street, address?.houseNumber, address?.building, address?.roomNumber].filter(el => el).join(", ")
                    return <Tooltip arrow placement="top" title={addressString}>
                        {address?.locality}
                    </Tooltip>
                },
            },)
            baseColumns.splice(2, 0, {
                header: 'Фактический адрес',
                id: 'residentialAddress',
                accessorKey: 'residentialAddress',
                Cell: ({cell, row}) => {
                    const address = cell.getValue()
                    const addressString = address && [country[address?.country], address?.locality, address?.street, address?.houseNumber, address?.building, address?.roomNumber].filter(el => el).join(", ")
                    return <Tooltip arrow placement="top" title={addressString}>
                        {address?.locality}
                    </Tooltip>
                },
            },)
        }
        if (itemId === "allOrganizations") {

            baseColumns.splice(2, 0, {
                id: 'dateOfLastVisit',
                header: 'Последнее посещения',
                accessorKey: 'dateOfLastVisit',
                size: 8,
                maxSize: 8,
                Cell: ({cell}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? '' : DateTimeUtil.dateConvert(value);
                },
            },);
            baseColumns.splice(2, 0, {
                id: 'registered',
                header: 'Регистрация',
                accessorKey: 'registered',
                size: 8,
                maxSize: 8,
            },)
            baseColumns.splice(2, 0, {
                header: 'Регион доставки',
                id: 'regionDelivery',
                accessorKey: 'regionDelivery',
            },)
            baseColumns.splice(1, 0, {
                id: 'country',
                header: 'Страна',
                accessorKey: "country",
                size: 30,
                grow: true,
                Cell: ({cell}) => country[cell.getValue()],
            },)
            baseColumns.splice(1, 0, {
                id: 'maxUserAmount',
                header: 'Лимит подключений',
                accessorKey: 'maxUserAmount',
                size: 8,
                maxSize: 8,
            },);
            baseColumns.splice(1, 0, {
                id: 'currUserAmount',
                header: 'Количество подключений',
                accessorKey: 'currUserAmount',
                size: 8,
                maxSize: 8,
            },);
        }

        if (itemId === "allUsers") {
            baseColumns.splice(2, 0, {
                header: 'Верифицирован',
                accessorKey: 'verified',
                enableColumnActions: false,
                enableEditing: false,
                size: 8,
                Cell: ({cell, row}) => <Checkbox
                    cell={cell}
                    checked={row.original.verified}
                    sx={{padding: "7px", '& .MuiSvgIcon-root': {fontSize: 22}}}
                >
                </Checkbox>,
            },);
            baseColumns.splice(2, 0, {
                id: 'lastActivity',
                header: 'Последняя активность',
                accessorKey: 'lastActivity',
                size: 8,
                maxSize: 8,
                Cell: ({cell}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? '' : DateTimeUtil.dateConvert(value);
                },
            });

        }
        return baseColumns;

    }, [data, itemId]);


    const menuItem = itemId === "allOrganizations" ? [
        {type: "edit", text: "Изменить лемит подключений", handler: handleEdit},
        {type: "delete", text: "Удалить", handler: handleConfirm}
    ] : [
        {type: "delete", text: "Удалить", handler: handleConfirm}
    ]

    return (<Box
        sx={{
            overflow: 'auto',
            maxWidth: 'calc(100vw - 410px)',
        }}
    >
        <Typography sx={{fontSize: "24px", fontWeight: 700, lineHeight: 1}}>{pageName}</Typography>
        <Box sx={{display: 'flex', justifyContent: "flex-end", padding: "30px  0"}}></Box>
        <MaterialReactTable
            renderTopToolbar={({table}) => {
                return <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "7px",
                    backgroundColor: (theme) => table.getSelectedRowModel().rows.length > 0 ? theme.palette.primary.light : "transparent",
                }}>
                    <MRT_ToolbarAlertBanner table={table}/>
                    <Box sx={{display: "flex"}}>
                        <MRT_ToggleFiltersButton table={table}/>
                        <MRT_GlobalFilterTextField table={table}/>
                    </Box>
                </Box>
            }}
            muiTableContainerProps={{
                sx: {
                    width: itemId === "allOrganizations" ? '2600px' : itemId === "allUsers" ? '1000px' : '1400px',
                    maxHeight: `calc(100vh - 420px)`
                }
            }}
            muiTableBodyCellProps={{
                sx: {
                    p: '0px 0px 0px 10px',
                    m: '0px 0px 0px 0px',
                    border: "none",
                    "&:after": {
                        display: "none",
                    },
                    "& td:after": {
                        "&:hover": {
                            backgroundColor: "red"
                        }
                    },
                    "&:not(:last-of-type)": {
                        borderRight: '1px solid #EAEBEA',

                    },
                    "&:first-of-type": {
                        padding: 0,
                        width: "50px",
                        height: "36px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: (theme) => theme.palette.text.light,
                        "& .MuiButtonBase-root": {
                            borderRadius: "4px",
                            width: "24px",
                            height: "24px",
                            opacity: 1,
                            margin: 0,
                            padding: 0,
                            "&:hover": {
                                backgroundColor: (theme) => theme.palette.primary.stroke,
                            }
                        }
                    },
                    "& li": {
                        listStyleType: "none",
                        "& a": {
                            color: "#006CCC",
                            textDecoration: "none",
                            "&:hover": {
                                textDecoration: "underline"
                            }
                        }
                    }
                },
            }}
            muiTableHeadCellProps={{
                sx: {
                    border: '1px solid #EAEBEA',
                    backgroundColor: "#F5F6FA",
                    padding: "10px",
                    paddingBottom: " 0.2rem",
                    paddingTop: "0.2rem"
                },
            }}
            muiTableBodyRowProps={({row}) => {
                return ({
                    sx: () => ({
                        height: '36px',
                        backgroundColor: "white",
                        "&:hover& td:after": {
                            display: "none",
                        },
                        "&.MuiTableRow-root.Mui-selected": {
                            backgroundColor: (theme) => theme.palette.primary.light70,
                            "&:hover": {
                                backgroundColor: (theme) => theme.palette.primary.light50,
                            },
                        },
                        "&:hover": {
                            "& td:after": {
                                display: "none",
                            },
                            backgroundColor: (theme) => theme.palette.primary.stroke,
                        },
                        '&:nth-of-type(even)': {
                            "&.MuiTableRow-root.Mui-selected": {
                                backgroundColor: (theme) => theme.palette.primary.light,
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.light50,
                                },
                            },
                            backgroundColor: (theme) => theme.palette.primary.lightGrey,
                            "&:hover": {
                                backgroundColor: (theme) => theme.palette.primary.stroke,
                            },
                        },
                    })
                })
            }}
            displayColumnDefOptions={{
                'mrt-row-actions': {
                    header: "",
                    size: 3,
                    minSize: 3,
                    grow: false,
                },
            }}
            muiSearchTextFieldProps={{
                placeholder: `Поиск`,
                sx: {
                    paddingTop: "5px",
                    paddingBottom: "0px",
                    "& .MuiCollapse-root": {
                        borderRadius: '12px',
                        height: 40, width: 290,
                        "& .MuiSvgIcon-root": {
                            color: "#737478"
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: 0
                        }
                    }
                },
                variant: 'outlined',
            }}
            columns={columns}
            data={data}
            rowHeight={13}
            columnSpacing={1}
            getRowId={(originalRow) => originalRow.id}
            selectionPadding={1}
            onPaginationChange={(updater) => {
                tableOptionsHandle(updater(tableOptions));
            }}
            state={{showGlobalFilter: true, pagination: tableOptions,}}
            rowCount={tableOptions.totalElements}
            selectionCss={{
                backgroundColor: 'lightblue',
                borderWidth: '2px',
                borderStyle: 'solid',
                borderColor: 'blue',
                borderRadius: '4px',
            }}
            initialState={{
                columnPinning: {right: ['enabled']}
            }}
            renderRowActionMenuItems={({closeMenu, row}) => menuItem.map(el => {
                return <MenuItem
                    key={el.type}
                    onClick={() => {
                        el.handler(row.original)
                        closeMenu();
                    }}
                    sx={{
                        margin: "0 8px",
                        borderRadius: "4px",
                        "&:hover": {backgroundColor: (theme) => theme.palette.primary.light}
                    }}
                >
                    {el.text}
                </MenuItem>
            })
            }
            localization={MRT_Localization_RU}
            enableRowActions={true}
            enableToolbarInternalActions={false}
        />
        <ModalConfirm
            title={"Подтвердить удаление?"}
            modalOpen={modalConfirm}
            handleClose={handleClose}
            handleSubmit={handleDelete}
            action={"Подтвердить"}/>
        <NameModal
            title={"Изменить"}
            label={"Лимит"}
            type={"number"}
            open={modal}
            onClose={handleClose}
            handleSubmit={handleUpdate}
            initValue={{name: currentEditRowData?.maxUserAmount}}
            loading={loading}/>
    </Box>);
};

export default AllUsers;